/**
 * TRAC-1035 — where the still-trying message is drawn, and what it says there.
 *
 * TRAC-1007 saves the message into the transcript a minute into the wait. Drawn as a
 * message, it was a block inside the "While we wait" card, above the "Did you know?" tip,
 * so the tip jumped down and the card grew at the moment the customer was already
 * impatient. It is still SAVED exactly as before (same id, same wording), so a reload, a
 * page change and a second tab all read it back the same way; it is just not drawn as a
 * message any more. It is drawn as one line under the "Connecting you to our … team now…"
 * heading instead, and that line holds its place from the start of the wait, so nothing
 * below the heading moves when it fills in.
 *
 * Pure: messages in, the line's place and content out. Whether the customer is still
 * waiting is ChatInterface's call.
 */
import type { Message } from '../../types/chat.types';
import { handoffSlowDepartment } from '../../constants/humanContact';
import { connectingLabelDepartment } from './handoffResume';

/**
 * The still-trying message, by the id ChatInterface's slow-wait effect saves it under:
 * `handoff-slow-${askedAt}`. Recognised by id rather than by a new flag so a transcript
 * saved before this change (a customer mid-wait during a deploy) is read the same way.
 */
export const isHandoffSlowNotice = (m: Pick<Message, 'id'>): boolean => m.id.startsWith('handoff-slow-');

export interface HandoffWaitStatus {
  /** The "Connecting you…" heading the line sits under: the newest one, this attempt's. */
  connectingId: string;
  /** False until the still-trying message is saved for this attempt: the line's place is held, empty. */
  shown: boolean;
  /** The team the line names. Once shown, the team the saved message was written for. */
  department: string | null;
}

const textOf = (m: Message): string => {
  const parts = (m.parts ?? []).filter((p) => p.type === 'text');
  if (parts.length === 0) return m.content || '';
  return parts.map((p) => (p as { content?: string }).content || '').join('');
};

/**
 * @param pendingDepartment the team this attempt is asking for, used while nothing is shown
 *   yet so the held space fits the line that will fill it.
 */
/** This attempt's "Connecting you…" heading: the newest one. -1 when the chat has none. */
const lastConnectingTurn = (messages: Message[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].isConnectingTurn) return i;
  }
  return -1;
};

export function handoffWaitStatus(messages: Message[], pendingDepartment: string | null): HandoffWaitStatus | null {
  const conn = lastConnectingTurn(messages);
  if (conn === -1) return null;
  const connectingId = messages[conn].id;
  // TRAC-1049: the heading itself names the team, in every tab. This page's own pick is better
  // when it has one (it is ahead of the heading while the backend is still confirming, and the
  // held space is sized from it), but where it has none the heading answers — so the line can
  // never say "a Tire Expert" with both numbers under a heading that says Sales.
  const headingDepartment = connectingLabelDepartment(messages[conn].content);
  // Only a message saved AFTER this heading belongs to this attempt; an earlier attempt's
  // stays in the transcript and is not drawn under the new one.
  for (let i = messages.length - 1; i > conn; i--) {
    if (isHandoffSlowNotice(messages[i])) {
      return { connectingId, shown: true, department: handoffSlowDepartment(textOf(messages[i])) ?? headingDepartment };
    }
  }
  return { connectingId, shown: false, department: pendingDepartment ?? headingDepartment };
}

/**
 * TRAC-1049 — the team the newest "Connecting you…" heading names, for the tab that is about to
 * SAVE the still-trying message: a tab that did not make the pick would otherwise save the
 * both-numbers wording into the shared transcript, and every other tab would read that back.
 */
export function connectingTurnDepartment(messages: Message[]): string | null {
  const conn = lastConnectingTurn(messages);
  return conn === -1 ? null : connectingLabelDepartment(messages[conn].content);
}
